// A full SWA-style day at the station: 311 inbound events spread across
// 24h with five connecting banks. Modeled to feel like an actual ramp
// dispatcher's schedule, not a hub-and-spoke synthetic dataset.
//
// Composition (sums to 311):
//   37  originators  — early-morning departures of aircraft that RON'd
//                      here last night. Modeled as inbound events with
//                      from='RON' so they take a gate slot in the demo.
//                      First originator is pinned to exactly 05:00.
//   30  Bank 1 (07:00) ±60m
//   25  inter-bank fill (08:00–10:00)
//   35  Bank 2 (11:00) ±60m   ← biggest mid-morning push
//   20  inter-bank fill (12:00–13:00)
//   30  Bank 3 (14:00) ±60m
//   20  inter-bank fill (15:00–16:00)
//   30  Bank 4 (17:00) ±60m
//   25  inter-bank fill (18:00–20:00)
//   22  Bank 5 (21:00) ±60m
//   37  terminators   — late-evening inbounds that RON tonight; no
//                      outbound stops, just unload. Spread 21:00 to
//                      00:45 next day; last terminator pinned to 00:45.
//
// Gates are explicitly scheduled with no double-booking: each inbound
// holds its arrival gate for a kind-aware window (see gateWindow), and
// the allocator refuses any placement that would overlap an existing
// reservation. With ~45 gates (C23–C67) and the SWA-ish 40-min turn,
// the day fits comfortably even at peak bank pressure.

const ORIGINS = [
  'MCO', 'PHX', 'LAS', 'SLC', 'MCI', 'AUS', 'HOU', 'SAN', 'DAL', 'STL',
  'BNA', 'BWI', 'MDW', 'DEN', 'OAK', 'SEA', 'PDX', 'ATL', 'TPA', 'FLL',
  'JAX', 'IAH', 'OMA', 'IND', 'MEM', 'CLE', 'CMH', 'PIT', 'BUF', 'BOS',
  'BDL', 'PVD', 'PHL', 'RDU', 'CHA', 'GSP', 'SJC', 'LAX',
];
const DESTS = [
  'EUG', 'BOI', 'ABQ', 'PDX', 'ELP', 'SEA', 'SLC', 'COS', 'LGB', 'BUR',
  'RNO', 'TUS', 'SMF', 'ICT', 'BIL', 'GEG', 'OKC', 'TUL', 'JAC', 'MSN',
  'GRR', 'DSM', 'DAY', 'TYS', 'BHM', 'MSY', 'SAT', 'CRP', 'AMA', 'LBB',
  'MAF', 'GJT', 'BZN', 'MSO', 'EYW', 'PNS', 'PBI', 'SFO',
];
const CATEGORIES = ['hot', 'normal', 'normal', 'normal', 'cold', 'priority', 'normal'];

// Day schedule. Order matters — generated inbounds are appended in
// schedule order so inb-N IDs roughly track time-of-day. Counts sum to
// 311. Terminator window stretches to 24*60+45 (00:45 next day).
const SCHEDULE = [
  { kind: 'originator', count: 37, startMin:  5 * 60,       endMin:  6 * 60 + 30 },
  { kind: 'normal',     count: 30, startMin:  6 * 60,       endMin:  8 * 60      }, // Bank 1 (07:00)
  { kind: 'normal',     count: 25, startMin:  8 * 60,       endMin: 10 * 60      },
  { kind: 'normal',     count: 35, startMin: 10 * 60,       endMin: 12 * 60      }, // Bank 2 (11:00)
  { kind: 'normal',     count: 20, startMin: 12 * 60,       endMin: 13 * 60      },
  { kind: 'normal',     count: 30, startMin: 13 * 60,       endMin: 15 * 60      }, // Bank 3 (14:00)
  { kind: 'normal',     count: 20, startMin: 15 * 60,       endMin: 16 * 60      },
  { kind: 'normal',     count: 30, startMin: 16 * 60,       endMin: 18 * 60      }, // Bank 4 (17:00)
  { kind: 'normal',     count: 25, startMin: 18 * 60,       endMin: 20 * 60      },
  { kind: 'normal',     count: 22, startMin: 20 * 60,       endMin: 22 * 60      }, // Bank 5 (21:00)
  { kind: 'terminator', count: 37, startMin: 21 * 60,       endMin: 24 * 60 + 45 },
];

// Gate range — physical concourse layout.
const GATE_NUMBERS = [];
for (let g = 23; g <= 67; g++) GATE_NUMBERS.push(g);

// Gate hold windows. Tuned so that the 311-flight day fits in 45 gates
// without conflicts at peak load (~37 simultaneous overnight aircraft).
const ORIGINATOR_HOLD_BEFORE = 240;  // 4h overnight presence before push
const TURN_MIN_LO            = 35;   // floor — fastest SWA turn
const TURN_MIN_HI            = 75;   // ceiling — heaviest turn (1h 15m)
const TERMINATOR_HOLD_AFTER  = 360;  // 6h after — sits overnight (RON)
// Minimum quiet time on a gate between consecutive aircraft. Baked
// into each reservation so the scheduler can't pack flights nose-to-tail.
const MIN_GAP_BETWEEN        = 25;

// Dispatcher's planning horizon. Demo "now" is noon, and the dispatcher
// stays 45 min – 1h15m out — anything arriving inside that window is
// the next batch to assign. Earlier flights are already on the grid
// (seed assignments); later flights are still on the day plan but
// haven't hit the queue yet. No artificial cap on the count — at peak
// banks the queue can naturally swell to a dozen+ flights, which is
// what makes the dispatcher's job interesting.
const PENDING_WINDOW = { startMin: 12 * 60 + 45, endMin: 13 * 60 + 15 };

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(r, arr) { return arr[Math.floor(r() * arr.length)]; }
function rand(r, lo, hi) { return Math.floor(r() * (hi - lo + 1)) + lo; }

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function fmtTime(mins) {
  // Wrap so 24*60+45 (00:45 next day) renders as "00:45".
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${pad(h)}:${pad(m)}`;
}

// Stops are populated in a second pass once every inbound's basic
// fields (gate, push time, flight number, next destination) are known.
// See populateRealStops below — pre-creating skeletons first means each
// stop can reference a REAL BANK inbound rather than a synthetic
// flight number, which keeps the feeders-by-outbound reverse index
// honest.

// Decide a run's role label given its slot index and the total number of
// runs on the inbound. Two-driver flights split FWD/AFT; three+ drivers
// fall back to "Drv N" since FWD/AFT only describes two compartments.
// A single-driver flight is "Solo" (no split).
function roleFor(slotIdx, totalRuns) {
  if (totalRuns === 1) return { slotLabel: 'Solo', splitType: null };
  if (totalRuns === 2) {
    return slotIdx === 0
      ? { slotLabel: 'FWD', splitType: 'FWD' }
      : { slotLabel: 'AFT', splitType: 'AFT' };
  }
  return { slotLabel: `Drv ${slotIdx + 1}`, splitType: null };
}

// Build a single inbound + its runs (skeleton — stops empty for normal
// turns and filled in later by populateRealStops). Originators and
// terminators never get transfer stops:
//   originator — synthetic morning departure (RON aircraft pushes off).
//                Driver runs load morning bags from the bag room; no
//                transfer fan-out, so stops stay empty.
//   terminator — last inbound of the day for the tail. No outbound;
//                driver runs unload to the bag room only.
//   normal     — full turnaround. Stops are populated against real
//                downstream BANK inbounds in populateRealStops.
function buildInbound(r, idIndex, kind, etaMins, gateNum, turnMins) {
  const isOriginator = kind === 'originator';
  const isTerminator = kind === 'terminator';

  const flightNum = rand(r, 100, 4900);
  const from = isOriginator ? 'RON' : pick(r, ORIGINS);
  const arrivalGate = `C${gateNum}`;
  const eta = fmtTime(etaMins);

  // Driver count varies by kind. Terminators are simple unloads (1 run).
  // Originators are typically 1–2 (no inbound bag rush). Normal turns
  // can fan out to 4 runs on heavy connections.
  const driverCount = isTerminator ? 1
                    : isOriginator ? rand(r, 1, 2)
                    : rand(r, 1, 4);

  // Turn time only meaningful for normals — originators/terminators have
  // their own gate-hold semantics (overnight in, overnight out).
  const effectiveTurn = isOriginator || isTerminator ? null : turnMins;
  // Where the aircraft heads on its outbound. Terminators stay
  // overnight so they have no next destination.
  const nextDestination = isTerminator ? null : pick(r, DESTS);
  const inbound = {
    id: `inb-${idIndex}`,
    flightNumber: `WN ${flightNum}`,
    from,
    nextDestination,
    arrivalGate,
    eta,
    etaMins,
    isOriginator,
    isTerminator,
    turnMins: effectiveTurn,
    pushMins: effectiveTurn != null ? etaMins + effectiveTurn : null,
    runs: [],
  };

  for (let d = 0; d < driverCount; d++) {
    const role = roleFor(d, driverCount);
    // Roughly 1 in 6 normal/originator runs is freight-only. Terminators
    // never are — they don't have outbound work.
    const isFreightOnly = !isTerminator && r() < 0.16;

    // Initial per-run bag count by kind. Normals get 0 here and get
    // their real total filled in by populateRealStops once the stops
    // exist. Originators/terminators carry synthetic counts since
    // they don't get transfer stops.
    let totalBags = 0;
    if (isFreightOnly)        totalBags = 0;
    else if (isTerminator)    totalBags = rand(r, 30, 90);   // bag-room unload
    else if (isOriginator)    totalBags = rand(r, 30, 90);   // morning load

    inbound.runs.push({
      id: `${inbound.id}-d${d + 1}`,
      inboundId: inbound.id,
      slot: d + 1,
      slotLabel: role.slotLabel,
      splitType: role.splitType,
      isFreightOnly,
      isOriginator,
      isTerminator,
      stops: [],
      totalBags,
      totalStops: 0,
      tightestConnection: 999,
      hasHot:      false,
      hasCold:     false,
      hasPriority: false,
    });
  }

  return inbound;
}

// Pass 4 of the build: for each normal-turn inbound, populate its
// runs' stops by picking REAL downstream BANK inbounds whose push
// time falls in a plausible connection window. The reverse index
// (feedersForOutbound) now resolves to actual flights, so the gate
// detail panel + map highlights are honest.
function populateRealStops(r, inbounds) {
  for (const A of inbounds) {
    if (A.isOriginator || A.isTerminator) continue;

    // Candidates: any other normal/originator inbound whose push lands
    // between 25 and 220 min after A lands. Originators ARE allowed as
    // candidates because their etaMins IS their push time — bags could
    // transfer onto a morning originator from a very early inbound.
    const candidates = inbounds.filter(B => {
      if (B.id === A.id) return false;
      if (B.isTerminator) return false;
      if (B.arrivalGate === A.arrivalGate) return false;
      // Originator B pushes at B.etaMins; normal B pushes at B.pushMins.
      const push = B.isOriginator ? B.etaMins : B.pushMins;
      if (push == null) return false;
      const conn = push - A.etaMins;
      return conn >= 25 && conn <= 220;
    });

    for (const run of A.runs) {
      if (run.isFreightOnly) continue;
      const stopCount = Math.min(rand(r, 3, 8), candidates.length);
      const stops = [];
      const usedIds = new Set();

      // Deterministic shuffle so different inbounds don't all pick the
      // same first-N candidates.
      const shuffled = candidates.slice();
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(r() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      for (const B of shuffled) {
        if (stops.length >= stopCount) break;
        if (usedIds.has(B.id)) continue;
        usedIds.add(B.id);
        const push = B.isOriginator ? B.etaMins : B.pushMins;
        const conn = push - A.etaMins;
        let cat;
        if      (conn <= 30)  cat = 'hot';
        else if (conn > 150)  cat = 'cold';
        else if (r() < 0.10)  cat = 'priority';
        else                  cat = 'normal';
        stops.push({
          flightNumber: B.flightNumber,
          destination:  B.nextDestination,
          gate:         B.arrivalGate,
          etd:          fmtTime(push),
          bags:         rand(r, 1, 18),
          connectionCategory: cat,
          connectionTime: conn,
        });
      }

      run.stops = stops;
      run.totalStops = stops.length;
      run.totalBags = stops.reduce((t, s) => t + s.bags, 0);
      run.tightestConnection = stops.length > 0
        ? stops.reduce((m, s) => Math.min(m, s.connectionTime), 999)
        : 999;
      run.hasHot      = stops.some(s => s.connectionCategory === 'hot');
      run.hasCold     = stops.some(s => s.connectionCategory === 'cold');
      run.hasPriority = stops.some(s => s.connectionCategory === 'priority');
    }
  }
}

// ── Gate scheduler ─────────────────────────────────────────────────
// Greedy allocator: for each inbound, scan a deterministically-shuffled
// gate order and reserve the first one whose occupancy doesn't overlap
// the inbound's hold window. Originators are processed first because
// they hold gates from before the day starts, then normals + terminators
// in eta order so the schedule fills naturally.

// The reservation extends past the aircraft's actual departure by
// MIN_GAP_BETWEEN so the next aircraft can't pull onto the gate until
// the quiet-time buffer has elapsed. Terminators don't need it (they
// sit overnight and the next aircraft is tomorrow's originator).
function gateWindow(kind, eta, turnMins) {
  if (kind === 'originator') return [eta - ORIGINATOR_HOLD_BEFORE, eta + 10 + MIN_GAP_BETWEEN];
  if (kind === 'terminator') return [eta, eta + TERMINATOR_HOLD_AFTER];
  return [eta, eta + turnMins + MIN_GAP_BETWEEN];
}

function allocateGates(raw, r) {
  // gate # → list of [start, end] reservations
  const occupancy = new Map(GATE_NUMBERS.map(g => [g, []]));

  const gateFree = (gate, start, end) =>
    !occupancy.get(gate).some(([s, e]) => start < e && end > s);
  const reserve = (gate, start, end) => {
    occupancy.get(gate).push([start, end]);
  };

  // Pick a gate for one inbound. Shuffled scan so the day spreads
  // across the concourse instead of crowding low gate numbers.
  const pickGate = (kind, eta, turnMins) => {
    const [start, end] = gateWindow(kind, eta, turnMins);
    const order = GATE_NUMBERS.slice();
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(r() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    for (const g of order) {
      if (gateFree(g, start, end)) {
        reserve(g, start, end);
        return g;
      }
    }
    // Fallback (shouldn't trip if the schedule is solvable): place at
    // the gate that frees up earliest.
    let best = order[0], bestEnd = Infinity;
    for (const g of order) {
      const list = occupancy.get(g);
      const lastEnd = list.length === 0 ? -Infinity : Math.max(...list.map(w => w[1]));
      if (lastEnd < bestEnd) { best = g; bestEnd = lastEnd; }
    }
    reserve(best, start, end);
    return best;
  };

  // Originators first — they need overnight gates secured before
  // anything else competes.
  const originators = raw.filter(x => x.kind === 'originator')
                         .sort((a, b) => a.etaMins - b.etaMins);
  const others      = raw.filter(x => x.kind !== 'originator')
                         .sort((a, b) => a.etaMins - b.etaMins);
  for (const x of originators) x.gateNum = pickGate(x.kind, x.etaMins, x.turnMins);
  for (const x of others)      x.gateNum = pickGate(x.kind, x.etaMins, x.turnMins);
}

function buildBank(seed = 42) {
  const r = rng(seed);

  // Pass 1: generate raw eta + kind + turn-time list from the schedule.
  // Turn time is sampled per-flight from [TURN_MIN_LO, TURN_MIN_HI] so
  // the gate-occupancy windows feel realistic instead of every aircraft
  // sitting for the same 40-min beat.
  const raw = [];
  for (const slot of SCHEDULE) {
    const span = slot.endMin - slot.startMin;
    for (let k = 0; k < slot.count; k++) {
      const t = (k + 0.5) / slot.count;
      const jitter = (r() - 0.5) * 6;  // ±3 minutes
      const etaMins = Math.max(
        slot.startMin,
        Math.min(slot.endMin - 1, Math.round(slot.startMin + t * span + jitter))
      );
      const turnMins = rand(r, TURN_MIN_LO, TURN_MIN_HI);
      raw.push({ kind: slot.kind, etaMins, turnMins });
    }
  }

  // Pin the day's first originator to exactly 05:00 …
  for (const x of raw) {
    if (x.kind === 'originator') { x.etaMins = 5 * 60; break; }
  }
  // … and the day's last terminator to exactly 00:45 next day.
  for (let i = raw.length - 1; i >= 0; i--) {
    if (raw[i].kind === 'terminator') { raw[i].etaMins = 24 * 60 + 45; break; }
  }

  // Pass 2: allocate gates with no overlapping reservations.
  allocateGates(raw, r);

  // Re-sort by eta so inb-N IDs track the day in chronological order.
  raw.sort((a, b) => a.etaMins - b.etaMins);

  // Pass 3: materialize each into a full inbound + runs (stops empty).
  let id = 1;
  const inbounds = raw.map(x =>
    buildInbound(r, id++, x.kind, x.etaMins, x.gateNum, x.turnMins)
  );

  // Pass 4: populate stops with REAL downstream BANK inbounds. Has to
  // happen after Pass 3 because each stop references another inbound's
  // flightNumber / gate / push time.
  populateRealStops(r, inbounds);

  return inbounds;
}

export const BANK = buildBank();

// Pending = inbounds in the dispatcher's near-horizon window (just past
// noon). These are what seat 2 just kicked over for assignment.
export const PENDING_INBOUND_IDS = new Set(
  BANK
    .filter(inb => inb.etaMins >= PENDING_WINDOW.startMin
                && inb.etaMins <= PENDING_WINDOW.endMin)
    .map(inb => inb.id)
);

function denormalizeRun(inb, run) {
  return {
    ...run,
    inboundFlightNumber: inb.flightNumber,
    inboundFrom: inb.from,
    inboundEta: inb.eta,
    inboundGate: inb.arrivalGate,
    inboundEtaMins: inb.etaMins,
  };
}

// Flat list of every run (one row per driver slot in the grid).
export const RUNS = BANK.flatMap(inb => inb.runs.map(run => denormalizeRun(inb, run)));

// Runs the seed assignment generator may pull from (excludes pending).
export const ASSIGNABLE_RUNS = BANK
  .filter(inb => !PENDING_INBOUND_IDS.has(inb.id))
  .flatMap(inb => inb.runs.map(run => denormalizeRun(inb, run)));

// Runs that show up in the queue drawer on first load.
export const PENDING_RUNS = BANK
  .filter(inb => PENDING_INBOUND_IDS.has(inb.id))
  .flatMap(inb => inb.runs.map(run => denormalizeRun(inb, run)));

// Lookup: run ID → run object (used by drawer, drop handlers, etc.)
export const RUNS_BY_ID = new Map(RUNS.map(r => [r.id, r]));

// Lookup: inbound ID → inbound (with raw, non-denormalized runs)
export const BANK_BY_ID = new Map(BANK.map(inb => [inb.id, inb]));

// Index by gate so the map can answer "what's coming next to C25?" in O(1)
// instead of scanning the whole BANK each render. Inbounds at each gate
// are sorted by ETA so callers can binary-search or just take .find().
const BANK_BY_GATE = (() => {
  const m = new Map();
  for (const inb of BANK) {
    const list = m.get(inb.arrivalGate) || [];
    list.push(inb);
    m.set(inb.arrivalGate, list);
  }
  for (const list of m.values()) list.sort((a, b) => a.etaMins - b.etaMins);
  return m;
})();

// Find the next inbound that will arrive at `gateId` strictly after
// `fromMins`. Returns null if nothing else is scheduled today.
export function nextInboundAtGate(gateId, fromMins) {
  const list = BANK_BY_GATE.get(gateId);
  if (!list) return null;
  return list.find(inb => inb.etaMins > fromMins) || null;
}

// Reverse index: outbound flight number → every inbound run that has a
// stop feeding bags to that outbound. Used by the gate-detail view to
// answer "where is this departure's freight coming from?".
const FEEDERS_BY_OUTBOUND = (() => {
  const m = new Map();
  for (const inb of BANK) {
    for (const run of inb.runs) {
      for (const stop of run.stops) {
        const list = m.get(stop.flightNumber) || [];
        list.push({
          inboundId: inb.id,
          inboundFlightNumber: inb.flightNumber,
          inboundFrom: inb.from,
          inboundGate: inb.arrivalGate,
          inboundEta: inb.eta,
          inboundEtaMins: inb.etaMins,
          runId: run.id,
          runSlotLabel: run.slotLabel,
          bags: stop.bags,
        });
        m.set(stop.flightNumber, list);
      }
    }
  }
  return m;
})();

// All inbound runs whose bags end up on `outboundFlightNumber`.
// Includes the originating-inbound entries (where this flight ITSELF
// is downloading bags into the bag room) and the transfer feeders.
export function feedersForOutbound(outboundFlightNumber) {
  return FEEDERS_BY_OUTBOUND.get(outboundFlightNumber) || [];
}
